import React, { useState, useRef, useEffect } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { createPortal } from "react-dom";

const NmCapaHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((s) => !s);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (drawerContentRef.current && !drawerContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-nm-capa"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content" ref={drawerContentRef}>
  <div className="drawer-header">
    <div className="header-title">
      <span className="emoji">🔧</span>
      <span className="title">NM CAPA Page – Help Guide</span>
    </div>
    <button className="close-button" onClick={toggle} aria-label="Close help panel">
      &times;
    </button>
  </div>

  <div className="drawer-body">
    <p>
      The NM CAPA Page manages all Corrective Actions and Preventive Actions (CAPA) raised during
      Near Miss investigations. It ensures accountability, timely closure, and tracking of progress.
      The page is divided into two tabs: <b>CAPA In-Progress</b> and <b>CAPA Completed</b>.
    </p>

    <hr />
    <h5 className="section-title">🟠  CAPA In-Progress Tab</h5>
    <ul className="steps-list" style={{ listStyleType: "disc", paddingLeft: "20px" }}>
      <li>Displays all CAPAs raised after the investigation team completes their review of a Near Miss.</li>
      <li>To see the CAPA list, click the Expand Icon on the record.</li>
      <li>Each CAPA record shows:</li>
      <ul style={{ listStyleType: "circle", paddingLeft: "20px" }}>
        <li>Corrective Action / Preventive Action Description</li>
        <li>Action Status: Approval Pending, Submitted for Approval, Approved, or Declined</li>
        <li>Priority of CAPA: Normal, Important, Urgent, Critical</li>
        <li>Overdue: Indicates if CAPA is not completed by the responsible person before the due date</li>
        <li>Category: Category under which CAPA is proposed</li>
        <li>Proposed By: User who suggested the CAPA</li>
        <li>Assigned To: Responsible person to implement the CAPA</li>
        <li>Due Date: Target date for CAPA completion</li>
        <li>Completion Percentage: Tracks implementation progress (e.g., 50% = half complete, 100% = fully complete)</li>
      </ul>
    </ul>

    <div className="alert alert-info" role="alert">
      👉 Once all CAPAs under a Near Miss reach 100% completion, they automatically move to the CAPA Completed Tab.
    </div>

    <hr />
    <h5 className="section-title">🟢 CAPA Completed Tab</h5>
    <ul className="steps-list" style={{ listStyleType: "disc", paddingLeft: "20px" }}>
      <li>Displays CAPAs that have reached 100% implementation.</li>
      <li>Each CAPA record shows:</li>
      <ul style={{ listStyleType: "circle", paddingLeft: "20px" }}>
        <li>Corrective Action / Preventive Action Description</li>
        <li>Action Status: Approved</li>
        <li>Priority Status: Normal, Important, Urgent, Critical</li>
        <li>Deviation: Indicates delay in completion beyond the due date (shown in number of days)</li>
        <li>Category: Category under which CAPA is proposed</li>
        <li>Proposed By: User who suggested the CAPA</li>
        <li>Assigned To: Responsible person for implementation</li>
        <li>Due Date: Target completion date</li>
        <li>Closing Date: Date when CAPA was approved and closed</li>
        <li>Evidence Submitted: Proof uploaded by the responsible person to confirm completion</li>
      </ul>
    </ul>

    <hr />
    <div className="help-tip">
      <p>
        ⚡ <b>Tip:</b> Use the status, priority, and deviation indicators to quickly identify
        high-risk CAPAs requiring urgent attention and ensure timely closure.
      </p>
    </div>
  </div>
</div>

      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default NmCapaHelpDrawer;
